import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { useAppState } from '../AppContext';
import Highlighter from 'react-highlight-words';
import { Button, Card, Spinner } from 'react-bootstrap';
import { clusterColor, copyToClipboard, getDisplayName } from './utils';
import { FaArrowLeft, FaCopy, FaRegFileAlt } from 'react-icons/fa';
import { GrContract } from 'react-icons/gr';
import { GiPlainCircle } from 'react-icons/gi/index';

const Snippet = ({ document, snip, highlightSnippet }) => {
  const [
    {
      selectedDocument,
      selectedSnippet,
      searchString,
      documentResults,
      documentCluster,
      documentClusterObj,
      clusterLabels
    }
  ] = useAppState();

  // Related snippets may be from a different document
  // TOOO: consider refactorin to remove the document property
  if (!documentResults) {
    return null;
  }
  let differentDocument = false;
  if ((!document || document.id !== snip.document_id) && snip.document_id) {
    document = documentResults.filter((d) => d.id === snip.document_id)[0];
    differentDocument = true;
  }

  const cluster_id = documentCluster[snip.document_id];
  const color = clusterColor(documentClusterObj[snip.document_id]);

  return (
    <>
      <div style={{ 'margin-right': '1em' }}>
        <GiPlainCircle color={color} />
      </div>

      <div>
        <Highlighter
          textToHighlight={snip.text}
          searchWords={searchString.split(' ')}
          highlightStyle={{ backgroundColor: '#ffd54f' }}
          style={{
            backgroundColor:
              snip.id === selectedSnippet ? 'rgb(232, 242, 252)' : null
          }}
        />
        &nbsp;{' '}
        <Button
          onClick={() => copyToClipboard(snip.text)}
          variant="light"
          size="sm"
        >
          <FaCopy />
        </Button>
        &nbsp;{' '}
        <p style={{ fontWeight: 'bold' }}>
          <FaRegFileAlt
            color={
              selectedDocument && document.id === selectedDocument.id
                ? 'gold'
                : differentDocument
                ? 'black'
                : 'lightblue'
            }
          />{' '}
          {getDisplayName(document)}
        </p>
        <p>
          <span style={{ fill: color }} className="keywords">
            {clusterLabels[cluster_id]}
          </span>
        </p>
        <Button
          onClick={() => highlightSnippet(snip, document)}
          variant="custom"
          size="sm"
        >
          <FaArrowLeft color={snip.id === selectedSnippet ? 'gold' : 'black'} />{' '}
          View
        </Button>{' '}
      </div>
    </>
  );
};

Snippet.propTypes = {
  document: PropTypes.shape({ id: PropTypes.number }), // also stuff for name
  snip: PropTypes.shape({
    document_id: PropTypes.number,
    id: PropTypes.number,
    text: PropTypes.string
  }),
  highlightSnippet: PropTypes.func
};

const RelatedSnippetsList = ({
  document,
  snippet_id,
  matchingDocumentIDs,
  highlightSnippet
}) => {
  let [{ selectedSnippet, searchString }, dispatch] = useAppState();

  const [loadingRelatedSnippets, setLoadingRelatedSnippets] = useState(false);
  const [relatedSnippets, setRelatedSnippets] = useState([]);

  useEffect(() => {
    setLoadingRelatedSnippets(true);
    fetch('/nlp/snippets/similar', {
      method: 'POST', // or 'PUT'
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        method: 'solr',
        snippetId: snippet_id,
        numRows: 5,
        textQuery: searchString
      })
    })
      .then((response) => response.json())
      .then((data) => {
        console.log('Success:', data);
        const relatedSnips = data.snippets.filter((s) => s.id !== snippet_id);
        relatedSnips.sort((a, b) => b.score - a.score);

        setRelatedSnippets(
          relatedSnips.filter((s) =>
            matchingDocumentIDs.includes(s.document_id)
          )
        );
        setLoadingRelatedSnippets(false);
        // setRelatedSnippets(relatedSnips);
      })
      .catch((error) => {
        console.error('Error on search:', error);
        setLoadingRelatedSnippets(false);
      });
  }, [searchString, snippet_id]);

  if (loadingRelatedSnippets) {
    return (
      <p>
        Searching for related snippets...
        <Spinner
          as="span"
          animation="border"
          role="status"
          aria-hidden="true"
        />
      </p>
    );
  }

  if (!relatedSnippets || relatedSnippets.length === 0) {
    return <p>No matching snippets.</p>;
  }

  return (
    <Card border="info" key={snippet_id} style={{ marginTop: '10px' }}>
      <Card.Header
        style={{
          backgroundColor:
            selectedSnippet && selectedSnippet.id === snippet_id
              ? 'rgba(255, 215, 0, 0.2)'
              : null
        }}
      >
        &nbsp;
        <Button
          variant="light"
          size="sm"
          onClick={() =>
            dispatch({
              type: 'SET_SHOW_RELATED_SNIPPETS',
              snippetId: snippet_id,
              value: false
            })
          }
        >
          <GrContract /> Hide
        </Button>
        <p>
          These snippets are taken from all relevant documents and ordered by
          similarity.
        </p>
      </Card.Header>

      <Card.Body>
        <div
          style={{
            display: 'grid',
            'grid-template-columns': '2em calc(100% - 2em)',
            'grid-gap': '10px'
          }}
        >
          {relatedSnippets.map((snip, i) => (
            <Snippet
              document={document}
              snip={snip}
              highlightSnippet={highlightSnippet}
              key={i}
            />
          ))}
        </div>
      </Card.Body>
    </Card>
  );
};

RelatedSnippetsList.propTypes = {
  document: PropTypes.object,
  snippet_id: PropTypes.number,
  matchingDocumentIDs: PropTypes.arrayOf(PropTypes.number),
  highlightSnippet: PropTypes.func
};

export default RelatedSnippetsList;
